import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { approverText, DOC_ICON, DOC_STEP, roleName, type ApprovalDoc } from '../../lib/approvals.ts';
import { money, parseMoney } from '../../lib/format.ts';
import { ManageLayout, useAction, useSave } from './common.tsx';

interface Rule {
  id: string;
  document: ApprovalDoc;
  min_amount: number;
  approver_role: string | null;
  approver_user: string | null;
  active: boolean;
  note: string | null;
}
interface Data {
  documents: { document: ApprovalDoc; rules: Rule[] }[];
  people: { id: string; name: string }[];
  roles: string[];
}

/**
 * Settings → Approvals: which documents need a second person's yes, from which amount, and who
 * gives it. Several steps on one document: the highest step an amount reaches decides.
 */
export function ApprovalRules() {
  const { data, reload } = useAction<Data>('studio.approval_rules');
  const [editing, setEditing] = useState<{ document: ApprovalDoc; rule: Rule | null } | null>(null);
  const nameOf = (id: string | null) => data?.people.find((p) => p.id === id)?.name ?? null;

  return (
    <ManageLayout title={t('manage.approvals')} subtitle={t('appr.rules_subtitle')}>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : !data.documents.length ? (
        <EmptyState icon="shieldCheck" title={t('appr.no_documents')} />
      ) : (
        data.documents.map(({ document, rules }) => {
          const on = rules.filter((r) => r.active);
          return (
            <section key={document} className="appr-section" aria-labelledby={`appr-${document}`}>
              <div className="appr-section-head">
                <span className="list-icon" aria-hidden="true">
                  <Icon name={DOC_ICON[document]} size={18} />
                </span>
                <div>
                  <h2 className="section-title" id={`appr-${document}`}>
                    {t(DOC_STEP[document])}
                  </h2>
                  <p className="muted small">{on.length ? tn('appr.steps', on.length) : t('appr.no_rule')}</p>
                </div>
                <Button size="sm" icon="plus" onClick={() => setEditing({ document, rule: null })}>
                  {t('appr.add_step')}
                </Button>
              </div>
              {rules.length ? (
                <div className="list-card" role="list">
                  {rules.map((r) => (
                    <div key={r.id} role="listitem" className={`list-row studio-row${r.active ? '' : ' archived'}`}>
                      <span className="list-icon" aria-hidden="true">
                        <Icon name={r.approver_user ? 'users' : 'shieldCheck'} size={18} />
                      </span>
                      <button className="list-main" onClick={() => setEditing({ document, rule: r })}>
                        <span className="list-title">{t('appr.from_amount', { amount: money(r.min_amount) })}</span>
                        <span className="list-sub">{[t('appr.approved_by', { who: approverText({ approver_role: r.approver_role, approver_name: nameOf(r.approver_user) }) }), r.note].filter(Boolean).join(' · ')}</span>
                      </button>
                      <span className="studio-end">{r.active ? null : <StatusBadge tone="neutral" icon="pause" label={t('appr.off')} />}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          );
        })
      )}
      {editing && data ? (
        <RuleDialog
          document={editing.document}
          rule={editing.rule}
          data={data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

function RuleDialog({ document, rule, data, onClose, onSaved }: { document: ApprovalDoc; rule: Rule | null; data: Data; onClose: () => void; onSaved: () => void }) {
  const { busy, run } = useSave();
  const [amount, setAmount] = useState(rule ? (rule.min_amount / 100).toFixed(2).replace(/\.00$/, '') : '');
  const [by, setBy] = useState<'role' | 'person'>(rule?.approver_user ? 'person' : 'role');
  const [role, setRole] = useState(rule?.approver_role ?? (data.roles.includes('manager') ? 'manager' : (data.roles[0] ?? '')));
  const [person, setPerson] = useState(rule?.approver_user ?? '');
  const [note, setNote] = useState(rule?.note ?? '');
  const [tried, setTried] = useState(false);
  const minor = parseMoney(amount);
  const errors = {
    amount: amount.trim() === '' ? t('form.required') : minor === null || minor < 0 ? t('appr.amount_wrong') : null,
    person: by === 'person' && !person ? t('form.required') : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const save = () => {
    setTried(true);
    if (invalid) return;
    void run(async () => {
      await action('studio.save_approval_rule', {
        ...(rule ? { id: rule.id } : {}),
        document,
        min_amount: minor,
        approver_role: by === 'role' ? role : null,
        approver_user: by === 'person' ? person : null,
        note: note.trim() || null,
      });
      onSaved();
    }, t('appr.rule_saved'));
  };
  const setActive = (active: boolean) =>
    void run(async () => {
      await action('studio.set_approval_rule_active', { id: rule!.id, active });
      onSaved();
    }, active ? t('appr.rule_on') : t('appr.rule_off'));

  return (
    <Dialog
      title={rule ? t('appr.edit_step') : t('appr.add_step')}
      onClose={onClose}
      footer={
        <>
          {rule ? (
            <Button variant="ghost" icon={rule.active ? 'pause' : 'undo'} className="dialog-left" onClick={() => setActive(!rule.active)} disabled={busy}>
              {rule.active ? t('appr.turn_off') : t('appr.turn_on')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={save} busy={busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <p className="muted">{t(DOC_STEP[document])}</p>
      <Field label={t('appr.min_amount')} hint={t('appr.min_amount_hint')} error={tried ? errors.amount : null} required>
        <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-autofocus />
      </Field>
      <div className="field">
        <span className="field-label" id="appr-by">
          {t('appr.who')}
        </span>
        <div className="segmented" role="radiogroup" aria-labelledby="appr-by">
          {(['role', 'person'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={by === k} className={by === k ? 'active' : ''} onClick={() => setBy(k)}>
              <Icon name={k === 'role' ? 'shieldCheck' : 'users'} size={16} />
              {k === 'role' ? t('appr.by_role') : t('appr.by_person')}
            </button>
          ))}
        </div>
      </div>
      {by === 'role' ? (
        <Field label={t('appr.role')} hint={t('appr.role_hint')}>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
            {data.roles.map((r) => (
              <option key={r} value={r}>
                {roleName(r)}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <Field label={t('appr.person')} error={tried ? errors.person : null} required>
          <select className="input" value={person} onChange={(e) => setPerson(e.target.value)}>
            <option value="">{t('appr.choose_person')}</option>
            {data.people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label={t('appr.rule_note')} hint={t('appr.rule_note_hint')}>
        <input className="input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <p className="appr-rule">
        <Icon name="shieldCheck" size={16} />
        <span>{t('appr.rule_preview', { amount: minor !== null && minor >= 0 ? money(minor) : '—', who: by === 'role' ? t('appr.any_role', { role: roleName(role) }) : (data.people.find((p) => p.id === person)?.name ?? '—') })}</span>
      </p>
    </Dialog>
  );
}
